/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useDebouncedCallback } from 'use-debounce';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ProjectIcon from '@atlaskit/icon/core/project';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Select, {
	components,
	type ControlProps,
	type OptionsType,
	type OptionType,
} from '@atlaskit/select';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { messages } from './messages';

const styles = cssMap({
	customControl: {
		width: '100%',
	},
});

const DEBOUNCE_DELAY = 300;

const extractProjectFromARI = (input: string): string => {
	// Match the last number digits after "project/"
	const pattern = /project\/(\d+)$/;
	const match = input.match(pattern);
	return match ? match[1] : '';
};

export const SearchJiraProjectsQuery = `
query GetProjects($query: String!, $siteAri: String!) {
  search {
		search(
			query: $query
			filters: {
				entities: ["ati:cloud:jira:project"],
				locations: [$siteAri],
			}
			experience: "rovo-chat"
		) {
			edges {
				node {
					... on SearchResultJiraProject {
						id
						title
						iconUrl
						url
						project {
							issueTypes {
								edges {
									node {
										issueTypeId
										name
										avatar {
											xsmall
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
}
`;

type SearchJiraProjectsResponse = {
	search: {
		search: {
			edges: {
				node: {
					id: string;
					title: string;
					iconUrl: string;
					url: string;
					project: {
						issueTypes: {
							edges: {
								node: {
									issueTypeId: string;
									name: string;
									avatar: {
										xsmall: string;
									};
								};
							}[];
						};
					};
				};
			}[];
		};
	};
};

type ProjectSelectorProps = {
	siteId: string;
	setProject: ({
		projectId,
		projectName,
		issueTypes,
	}: {
		projectId: string;
		projectName: string;
		issueTypes: { issueTypeId: string; name: string; avatarUrl: string }[];
	}) => void;
	selectedProject?: OptionType;
	isDisabled?: boolean;
};

/**
 * This component is used to select a project from Jira.
 * This component has been copied over from the jira issue-create action /jira-issue-create/jira-issue-create-action-unified-renderer/project-selector/index.tsx
 * It was modified to remove the InitialProjectId and IssueTypes props.
 * @param siteId - The site ID of the Jira instance.
 * @param setProject - The function to set the project.
 * @param selectedProject - The selected project.
 * @param isDisabled - Whether the project selector is disabled.
 */
export const ProjectSelector = ({
	siteId,
	setProject,
	selectedProject,
	isDisabled = false,
}: ProjectSelectorProps): JSX.Element => {
	di(performGqlFetch, useAnalytics);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [allOptions, setAllOptions] = useState<OptionsType>([]);
	const [displayedOptions, setDisplayedOptions] = useState<OptionsType>([]);
	const [isLoading, setIsLoading] = useState(false);

	const loadOptions = useCallback(
		async (value: string): Promise<OptionsType> => {
			setIsLoading(true);
			try {
				const response = await performGqlFetch<
					SearchJiraProjectsResponse,
					{
						query: string;
						siteAri: string;
					}
				>({
					gqlQuery: SearchJiraProjectsQuery,
					variables: {
						query: value,
						siteAri: `ari:cloud:confluence::site/${siteId}`,
					},
				});

				return (
					response.data?.search?.search?.edges
						?.map((edge) => edge.node)
						?.map((item) => ({
							label: item.title,
							value: item.id,
							issueTypes: item.project.issueTypes.edges.map(({ node }) => ({
								issueTypeId: node.issueTypeId,
								name: node.name,
								avatarUrl: node.avatar?.xsmall,
							})),
						})) ?? []
				);
			} catch (error) {
				logException({
					exception: error,
					name: 'SearchJiraProjectsQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			} finally {
				setIsLoading(false);
			}
		},
		[siteId],
	);

	const sortOptionsWithSelectedFirst = useCallback(
		(options: OptionsType): OptionsType => {
			if (!selectedProject?.value || !options || options.length === 0) {
				return options || [];
			}

			const otherOptions = options.filter((option) => {
				let optionValue = option.value;
				if (typeof option.value === 'string') {
					optionValue = extractProjectFromARI(option.value) || option.value;
				}
				return optionValue !== selectedProject.value;
			});

			return [selectedProject, ...otherOptions];
		},
		[selectedProject],
	);

	// Preload initial options when component mounts
	useEffect(() => {
		const preloadOptions = async () => {
			if (allOptions.length === 0 && !isLoading) {
				const initialOptions = await loadOptions('');
				setAllOptions(initialOptions);
				setDisplayedOptions(initialOptions);
			}
		};

		preloadOptions();
	}, [allOptions.length, isLoading, loadOptions]);

	const handleProjectChange = useCallback(
		(selectedOption: OptionType | null) => {
			const selectedOptionValue = selectedOption?.value;
			if (!selectedOption || typeof selectedOptionValue !== 'string') {
				return;
			}

			const selectedOptionProjectId =
				extractProjectFromARI(selectedOptionValue) || selectedOptionValue;
			if (selectedOptionProjectId === selectedProject?.value) {
				return;
			}

			setProject({
				projectId: selectedOptionProjectId,
				projectName: selectedOption.label ?? '',
				issueTypes: selectedOption.issueTypes ?? [],
			});

			if (!allOptions.some((option) => option.value === selectedOptionValue)) {
				setAllOptions((prev) => [...prev, selectedOption]);
			}
			setDisplayedOptions(allOptions);
		},
		[selectedProject, setProject, allOptions],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(async (value: string) => {
		setDisplayedOptions(await loadOptions(value));
	}, DEBOUNCE_DELAY);

	const handleInputChange = useCallback(
		(inputValue: string, { action }: { action: string }) => {
			if (action === 'input-change') {
				debouncedLoadOptions(inputValue);
			}
		},
		[debouncedLoadOptions],
	);

	return (
		<>
			<Heading as="h2" size="xsmall">
				{formatMessage(messages.projectSelectorHeader)}
			</Heading>
			<Select
				testId="require-information-tool-project-selector"
				spacing="compact"
				appearance="subtle"
				value={selectedProject}
				isLoading={isLoading}
				options={sortOptionsWithSelectedFirst(displayedOptions)}
				onChange={handleProjectChange}
				onInputChange={handleInputChange}
				onMenuOpen={() => {
					sendAnalyticsEvent({
						action: 'opened',
						actionSubject: 'dropdown',
						actionSubjectId: 'rovoRequireInformationToolProjectSelector',
					});
				}}
				onMenuClose={() => {
					setDisplayedOptions(allOptions);
				}}
				components={{
					Control: CustomControl,
				}}
				disabled={isDisabled}
			/>
		</>
	);
};

const CustomControl = ({ children, ...props }: ControlProps<OptionType>) => {
	return (
		<components.Control {...props}>
			<Box paddingInline="space.100" xcss={styles.customControl}>
				<Inline alignBlock="center">
					<ProjectIcon label="" />
					{children}
				</Inline>
			</Box>
		</components.Control>
	);
};
